import React, { useCallback } from 'react';
import { Card, Stack } from '@mantine/core';
import { useRouter } from 'next/router';

import { CustomLoadingOverlay } from '~/components/shared/Loaders/CustomLoadingOverlay';
import { EO_BRANDS } from '~/domains/auth/consts';
import dt from '~/testing';

import useStyles from './ContestRestartCard.styles';
import type { ContestRestartCardProps } from './types';
import ContestInviteCardSourceInfo from './ContestRestartCardSourceInfo';
import ContestInviteCardContestInfo from './ContestRestartCardContestInfo';
import ContestInviteCardActionButtons from './ContestRestartCardActionButtons';

function ContestRestartCard({
  restart,
  onRestart,
  onDismiss,
  onContestClick,
}: ContestRestartCardProps) {
  const { classes } = useStyles();
  const router = useRouter();
  const { contest } = restart;

  const isEoBrand = restart.source === EO_BRANDS.RYP || restart.source === EO_BRANDS.OFP;

  const handleRestart = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      onRestart?.(contest.restart_url);
    },
    [onRestart, router, contest]
  );

  const handleDismiss = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      onDismiss?.(contest.id);
    },
    [onDismiss, contest]
  );

  const handleContestClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      onContestClick?.(contest.game_center_url);
    },
    [onContestClick, contest]
  );

  return (
    <Card
      className={classes.container}
      p="md"
      radius="lg"
      data-test-id={dt.contest.common.contestRestartCard}
      onClick={handleContestClick}
      style={{ cursor: 'pointer' }}
      component="div"
    >
      <CustomLoadingOverlay isFetching={restart.isLoadingContest} isPreviousData opacity={0.8} />
      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={12}>
        {isEoBrand && <ContestInviteCardSourceInfo source={restart.source} />}
        <ContestInviteCardContestInfo contest={restart.contest} source={restart.source} />
        <ContestInviteCardActionButtons
          handleRestart={handleRestart}
          handleDismiss={handleDismiss}
          isLoading={restart.isLoadingActions}
        />
      </Stack>
    </Card>
  );
}

export default ContestRestartCard;
